import React, { useState, useEffect } from 'react';
import axios from './lib/axiosClient';
import { fmtDate as fmtDateShared } from './lib/fmtDate';

const C = {
  bg:'#1a1c28', card:'#252838', border:'#2e3150', text:'#e2e8f0',
  muted:'#64748b', accent:'#f97316', green:'#4ade80', red:'#f87171',
  blue:'#60a5fa', purple:'#a78bfa',
};
const PLAN_INFO = {
  founding:{ name:'Founding Member', price:199, color:'#f97316', badge:'🔥 Founding Rate — Locked Forever' },
  standard:{ name:'Standard',        price:299, color:'#60a5fa', badge:'Standard Plan' },
  trial:   { name:'Free Trial',       price:0,   color:'#a78bfa', badge:'🧪 Trial' },
};
const fmt     = (n) => `$${Number(n||0).toFixed(2)}`;
const fmtDate = (d) => fmtDateShared(d, {month:'long',day:'numeric',year:'numeric'});

// ─────────────────────────────────────────────────────────────────────────────
// ADD THIS COMPONENT to Billing.js — paste before "export default Billing"
// Then add <ConnectPayments /> inside the Billing component return
// ─────────────────────────────────────────────────────────────────────────────

function ConnectPayments({ axios }) {
  const [status,  setStatus]  = useState(null);   // null | {connected, chargesEnabled, payoutsEnabled}
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const params = new URLSearchParams(window.location.search);
  const connectResult = params.get('connect'); // 'success' | 'refresh'

  useEffect(() => {
    axios.get('/api/billing/connect/status')
      .then(r => setStatus(r.data))
      .catch(() => setStatus({ connected: false }))
      .finally(() => setLoading(false));
  }, []);

  const handleOnboard = async () => {
    setWorking(true);
    try {
      const r = await axios.post('/api/billing/connect/onboard');
      if (r.data.url) window.location.href = r.data.url;
    } catch(e) { alert('Could not start onboarding. Try again.'); }
    setWorking(false);
  };

  const handleDashboard = async () => {
    setWorking(true);
    try {
      const r = await axios.post('/api/billing/connect/dashboard');
      if (r.data.url) window.open(r.data.url, '_blank');
    } catch(e) { alert('Could not open dashboard.'); }
    setWorking(false);
  };

  const S = {
    card:  { background:'#252838', border:'1px solid #2e3150', borderRadius:12, padding:24, marginBottom:20 },
    title: { fontSize:15, fontWeight:700, color:'#f1f5f9', marginBottom:4 },
    sub:   { fontSize:12, color:'#64748b', marginBottom:20 },
    row:   { display:'flex', alignItems:'center', gap:10, marginBottom:12 },
    dot:   (on) => ({ width:8, height:8, borderRadius:'50%', background: on ? '#34d399' : '#475569', flexShrink:0 }),
    lbl:   { fontSize:13, color:'#e2e8f0' },
    btn:   (v) => ({ padding:'9px 20px', borderRadius:6, fontSize:13, fontWeight:700, cursor:'pointer', border:'none',
      background: v==='primary' ? 'linear-gradient(135deg,#f97316,#ea580c)' : '#2e3150',
      color: v==='primary' ? '#fff' : '#94a3b8' }),
    badge: (ok) => ({ display:'inline-block', padding:'3px 10px', borderRadius:20, fontSize:11, fontWeight:700,
      background: ok ? 'rgba(52,211,153,0.1)' : 'rgba(71,85,105,0.2)',
      color: ok ? '#34d399' : '#64748b', border: `1px solid ${ok ? '#34d399' : '#3a3f60'}` }),
    fee:   { background:'rgba(249,115,22,0.06)', border:'1px solid rgba(249,115,22,0.15)', borderRadius:8, padding:'10px 14px', fontSize:12, color:'#fb923c', marginBottom:16 },
  };

  if (loading) return null;

  return (
    <div style={S.card}>
      <div style={S.title}>💳 Accept Payments</div>
      <div style={S.sub}>Connect your bank account to accept payments from clients directly through IVision.</div>

      {connectResult === 'success' && (
        <div style={{background:'rgba(52,211,153,0.1)',border:'1px solid #34d399',borderRadius:8,padding:'10px 14px',fontSize:12,color:'#34d399',marginBottom:16}}>
          ✅ Payment account connected! It may take a few minutes to fully activate.
        </div>
      )}
      {connectResult === 'refresh' && (
        <div style={{background:'rgba(249,115,22,0.1)',border:'1px solid #f97316',borderRadius:8,padding:'10px 14px',fontSize:12,color:'#fb923c',marginBottom:16}}>
          ⚠️ Onboarding expired. Please try connecting again.
        </div>
      )}

      <div style={S.fee}>
        💡 IVision charges a <strong>0.75% platform fee</strong> on payments processed through the platform — on top of standard Stripe fees (2.9% + 30¢). You keep the rest.
      </div>

      {!status?.connected ? (
        <div>
          <div style={{...S.row, marginBottom:20}}>
            <div style={S.dot(false)} />
            <span style={S.lbl}>No payment account connected</span>
          </div>
          <button style={S.btn('primary')} onClick={handleOnboard} disabled={working}>
            {working ? 'Opening...' : '🔗 Connect Payment Account'}
          </button>
        </div>
      ) : (
        <div>
          <div style={S.row}>
            <div style={S.dot(true)} />
            <span style={S.lbl}>Payment account connected</span>
            <span style={S.badge(status.chargesEnabled)}>{status.chargesEnabled ? 'Active' : 'Pending'}</span>
          </div>
          <div style={S.row}>
            <div style={S.dot(status.chargesEnabled)} />
            <span style={S.lbl}>Accepting payments</span>
          </div>
          <div style={S.row}>
            <div style={S.dot(status.payoutsEnabled)} />
            <span style={S.lbl}>Payouts to bank</span>
          </div>
          <div style={{display:'flex', gap:10, marginTop:16}}>
            {status.chargesEnabled ? (
              <button style={S.btn('primary')} onClick={handleDashboard} disabled={working}>
                {working ? 'Opening...' : '📊 View Payment Dashboard'}
              </button>
            ) : (
              <button style={S.btn('primary')} onClick={handleOnboard} disabled={working}>
                {working ? 'Opening...' : '⚙️ Complete Setup'}
              </button>
            )}
          </div>
          {!status.chargesEnabled && (
            <div style={{fontSize:11,color:'#64748b',marginTop:10}}>
              Stripe is reviewing your account. This usually takes a few minutes to a few hours.
            </div>
          )}
        </div>
      )}
    </div>
  );
}


export default function Billing() {
  const [billing,         setBilling]         = useState(null);
  const [invoices,        setInvoices]        = useState([]);
  const [loading,         setLoading]         = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [portalLoading,   setPortalLoading]   = useState(false);
  const [tab,             setTab]             = useState('overview');

  const params       = new URLSearchParams(window.location.search);
  const showSuccess  = params.get('success')  === 'true';
  const showCanceled = params.get('canceled') === 'true';

  useEffect(()=>{ load(); },[]);

  const load = async () => {
    setLoading(true);
    try {
      const statusRes = await axios.get('/api/billing/status');
      setBilling(statusRes.data.billing || null);
      try { const r = await axios.get('/api/billing/invoices'); setInvoices(r.data.invoices||[]); } catch(_){}
    } catch(e){ console.error(e); }
    setLoading(false);
  };

  const handleSubscribe = async (planId) => {
    setCheckoutLoading(planId);
    try {
      const res = await axios.post('/api/billing/create-checkout',{planId});
      if(res.data.url) window.location.href = res.data.url;
    } catch(e){ alert(e.response?.data?.error||'Stripe is not yet in live mode. Contact support to activate.'); }
    setCheckoutLoading(null);
  };

  const handlePortal = async () => {
    setPortalLoading(true);
    try { const r=await axios.post('/api/billing/create-portal'); if(r.data.url) window.location.href=r.data.url; }
    catch(e){ alert('Billing portal unavailable. Contact support.'); }
    setPortalLoading(false);
  };

  if(loading) return <div style={{display:'flex',justifyContent:'center',alignItems:'center',minHeight:'60vh',fontFamily:"'DM Sans',Arial,sans-serif"}}><div style={{color:C.muted}}>Loading subscription...</div></div>;

  const plan     = billing?.plan||'trial';
  const planInfo = PLAN_INFO[plan]||PLAN_INFO.trial;
  const isPaying = ['founding','standard'].includes(plan);
  const nextBill = billing?.subscription?.current_period_end;
  const canceling = billing?.subscription?.cancel_at_period_end;

  const S = {
    wrap:  {fontFamily:"'DM Sans',Arial,sans-serif",background:C.bg,minHeight:'100vh',padding:'32px 24px',maxWidth:860,margin:'0 auto'},
    card:  {background:C.card,border:`1px solid ${C.border}`,borderRadius:14,padding:24,marginBottom:20},
    tabs:  {display:'flex',gap:4,marginBottom:24,background:C.card,borderRadius:10,padding:4,border:`1px solid ${C.border}`},
    tab:   (a)=>({flex:1,padding:'9px 0',textAlign:'center',borderRadius:8,border:'none',fontWeight:600,fontSize:13,cursor:'pointer',background:a?C.accent:'transparent',color:a?'#fff':C.muted}),
    pill:  (color)=>({display:'inline-block',padding:'3px 12px',borderRadius:20,fontSize:11,fontWeight:700,background:color+'22',color}),
    btn:   (v)=>({padding:v==='lg'?'13px 28px':'9px 18px',borderRadius:8,border:v==='outline'?`1px solid ${C.border}`:'none',fontWeight:700,fontSize:v==='lg'?15:13,cursor:'pointer',background:v==='outline'?'transparent':v==='ghost'?'transparent':C.accent,color:v==='outline'?C.muted:v==='ghost'?C.muted:'#fff'}),
    h2:    {fontSize:18,fontWeight:800,color:C.text,margin:'0 0 16px'},
    th:    {padding:'10px 14px',textAlign:'left',fontSize:11,color:C.muted,fontWeight:600,textTransform:'uppercase',letterSpacing:'.4px',borderBottom:`1px solid ${C.border}`},
    td:    {padding:'12px 14px',fontSize:13,color:C.text,borderBottom:`1px solid ${C.border}`},
  };

  return (
    <div style={S.wrap}>
      <div style={{marginBottom:28}}>
        <div style={{fontSize:24,fontWeight:800,color:C.text,marginBottom:4}}>Subscription & Billing</div>
        <div style={{fontSize:14,color:C.muted}}>Manage your iVision CRM plan and payment method</div>
      </div>

      {showSuccess && <div style={{background:'#14532d',border:`1px solid ${C.green}`,borderRadius:10,padding:16,marginBottom:20,color:C.green,fontWeight:600}}>🎉 Subscription activated! Your plan is now live.</div>}
      {showCanceled && <div style={{background:'#3b1a00',border:'1px solid #f97316',borderRadius:10,padding:16,marginBottom:20,color:'#fdba74'}}>Checkout was canceled. You can try again anytime below.</div>}
      {canceling && <div style={{background:'#3b0a0a',border:`1px solid ${C.red}`,borderRadius:10,padding:16,marginBottom:20,color:C.red,fontWeight:600}}>⚠️ Your subscription cancels on {fmtDate(nextBill)}. You'll keep access until then.</div>}

      <div style={S.tabs}>
        {[['overview','📊 Overview'],['plans','🔄 Plans'],['invoices','🧾 Invoices']].map(([k,l])=>(
          <button key={k} style={S.tab(tab===k)} onClick={()=>setTab(k)}>{l}</button>
        ))}
      </div>

      {tab==='overview' && <>
        {/* Current plan */}
        <div style={{...S.card,border:`2px solid ${planInfo.color}40`,background:`linear-gradient(135deg,${C.card},#1a1c2e)`}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',flexWrap:'wrap',gap:12}}>
            <div>
              <span style={S.pill(planInfo.color)}>{planInfo.badge}</span>
              <div style={{fontSize:26,fontWeight:800,color:C.text,margin:'10px 0 4px'}}>{planInfo.name}</div>
              {isPaying
                ? <div style={{fontSize:30,fontWeight:800,color:planInfo.color}}>${planInfo.price}<span style={{fontSize:14,color:C.muted,fontWeight:400}}>/mo</span></div>
                : <div style={{fontSize:18,color:C.purple,fontWeight:700}}>Free Trial {billing?.trialDaysLeft>0?`· ${billing.trialDaysLeft} days left`:'· Expired'}</div>
              }
            </div>
            {isPaying && <button style={S.btn('outline')} onClick={handlePortal} disabled={portalLoading}>{portalLoading?'Opening...':'⚙️ Manage Billing'}</button>}
          </div>

          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(155px,1fr))',gap:12,marginTop:20}}>
            {[
              ['Status',    isPaying?'✅ Active':'🧪 Trial'],
              ['Next Bill', isPaying&&nextBill?fmtDate(nextBill):'—'],
              ['Amount',    isPaying?fmt(planInfo.price):'$0.00'],
              ['Payment',   billing?.hasPaymentMethod?'✓ Card on file':'No card yet'],
            ].map(([lbl,val])=>(
              <div key={lbl} style={{background:'#181928',borderRadius:10,padding:'12px 16px'}}>
                <div style={{fontSize:10,color:C.muted,textTransform:'uppercase',letterSpacing:'.5px',marginBottom:4}}>{lbl}</div>
                <div style={{fontSize:14,fontWeight:700,color:C.text}}>{val}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Features */}
        <div style={S.card}>
          <div style={S.h2}>What's Included</div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:0}}>
            {[
              '✅ Unlimited equipment catalog',
              '✅ Online storefront',
              '✅ Order & contract management',
              '✅ Quote creation & e-signature',
              '✅ Client CRM',
              '✅ Invoice & payment tracking',
              '✅ Delivery routing & pricing',
              '✅ Reports & analytics',
              '✅ Email notifications',
              '✅ Import / Export tools',
              '✅ 200 SMS/mo included',
              '✅ Unlimited staff accounts',
            ].map((f,i)=>(
              <div key={i} style={{fontSize:13,color:C.text,padding:'8px 0',borderBottom:`1px solid ${C.border}`}}>{f}</div>
            ))}
          </div>
        </div>

        {!isPaying && (
          <div style={{...S.card,textAlign:'center',border:`1px solid ${C.accent}40`,padding:32}}>
            <div style={{fontSize:22,fontWeight:800,color:C.text,marginBottom:8}}>Ready to go live?</div>
            <div style={{fontSize:14,color:C.muted,marginBottom:24}}>Lock in the Founding Member rate at $199/mo before spots fill up. Your rate is locked forever.</div>
            <button style={S.btn('lg')} onClick={()=>handleSubscribe('founding')} disabled={checkoutLoading==='founding'}>
              {checkoutLoading==='founding'?'Loading...':'🔒 Claim $199 Founding Rate →'}
            </button>
          </div>
        )}
      </>}
      <ConnectPayments axios={axios} />

      {tab==='plans' && (
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:20}}>
          <div style={{...S.card,border:`2px solid ${C.accent}`,position:'relative'}}>
            <div style={{position:'absolute',top:-13,left:'50%',transform:'translateX(-50%)',background:C.accent,color:'#fff',padding:'3px 16px',borderRadius:20,fontSize:11,fontWeight:700}}>LIMITED SPOTS</div>
            <div style={{color:C.accent,fontSize:11,fontWeight:700,letterSpacing:1,marginBottom:8}}>FOUNDING MEMBER</div>
            <div style={{fontSize:38,fontWeight:800,color:C.text}}>$199<span style={{fontSize:14,color:C.muted,fontWeight:400}}>/mo</span></div>
            <div style={{fontSize:12,color:C.muted,marginBottom:4}}><s style={{color:'#3a3f60'}}>$299/mo</s> <span style={{color:C.accent,fontWeight:600}}>Save 33% · Rate locked forever</span></div>
            <div style={{fontSize:13,color:'#94a3b8',marginBottom:20}}>Everything included · Unlimited staff · Priority support</div>
            {plan==='founding'
              ? <div style={{textAlign:'center',padding:12,background:'#14532d',borderRadius:8,color:C.green,fontWeight:700}}>✓ Your Current Plan</div>
              : <button style={{...S.btn('lg'),width:'100%'}} onClick={()=>handleSubscribe('founding')} disabled={!!checkoutLoading}>{checkoutLoading==='founding'?'Loading...':'Claim $199 Founding Rate →'}</button>
            }
          </div>
          <div style={S.card}>
            <div style={{color:C.muted,fontSize:11,fontWeight:700,letterSpacing:1,marginBottom:8}}>STANDARD</div>
            <div style={{fontSize:38,fontWeight:800,color:C.text}}>$299<span style={{fontSize:14,color:C.muted,fontWeight:400}}>/mo</span></div>
            <div style={{fontSize:12,color:C.muted,marginBottom:4}}>Full price — all features included</div>
            <div style={{fontSize:13,color:'#94a3b8',marginBottom:20}}>Everything included · Unlimited staff · Standard support</div>
            {plan==='standard'
              ? <div style={{textAlign:'center',padding:12,background:'#14532d',borderRadius:8,color:C.green,fontWeight:700}}>✓ Your Current Plan</div>
              : <button style={{...S.btn('lg'),width:'100%',background:'#2e3150',color:C.text}} onClick={()=>handleSubscribe('standard')} disabled={!!checkoutLoading}>{checkoutLoading==='standard'?'Loading...':'Get Started at $299 →'}</button>
            }
          </div>
          <div style={{...S.card,gridColumn:'1/-1'}}>
            <div style={S.h2}>SMS Add-ons <span style={{fontSize:12,color:C.muted,fontWeight:400}}>200 SMS included free</span></div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12}}>
              {[{texts:'1,000',price:29},{texts:'3,000',price:49},{texts:'5,000',price:79}].map(t=>(
                <div key={t.texts} style={{background:'#181928',borderRadius:10,padding:16,textAlign:'center'}}>
                  <div style={{fontSize:20,fontWeight:800,color:C.purple}}>${t.price}<span style={{fontSize:12,color:C.muted}}>/mo</span></div>
                  <div style={{fontSize:13,color:C.text,marginTop:4}}>{t.texts} texts/month</div>
                  <div style={{fontSize:11,color:C.muted,marginTop:2}}>$0.02/text overage</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab==='invoices' && (
        <div style={S.card}>
          <div style={S.h2}>Invoice History</div>
          {invoices.length===0 ? (
            <div style={{textAlign:'center',padding:'40px 0',color:C.muted}}>
              <div style={{fontSize:36,marginBottom:12}}>🧾</div>
              <div style={{fontSize:15,marginBottom:6}}>No invoices yet</div>
              <div style={{fontSize:13}}>{isPaying?'Invoices will appear here once Stripe billing goes live.':'Invoices will appear after your first payment.'}</div>
            </div>
          ) : (
            <table style={{width:'100%',borderCollapse:'collapse'}}>
              <thead><tr style={{background:'#181928'}}>
                {['Date','Description','Amount','Status',''].map(h=><th key={h} style={S.th}>{h}</th>)}
              </tr></thead>
              <tbody>{invoices.map((inv,i)=>(
                <tr key={i}>
                  <td style={S.td}>{fmtDate(inv.date)}</td>
                  <td style={S.td}>{inv.description||'iVision CRM Subscription'}</td>
                  <td style={{...S.td,fontWeight:700}}>{fmt(inv.amount)}</td>
                  <td style={S.td}><span style={S.pill(inv.status==='paid'?C.green:C.red)}>{inv.status}</span></td>
                  <td style={S.td}>{inv.pdf_url&&<a href={inv.pdf_url} target="_blank" rel="noreferrer" style={{fontSize:12,color:C.blue,textDecoration:'none',fontWeight:600}}>PDF</a>}</td>
                </tr>
              ))}</tbody>
            </table>
          )}
          <div style={{marginTop:20,padding:14,background:'#181928',borderRadius:10,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <div>
              <div style={{fontSize:13,fontWeight:600,color:C.text}}>Questions about your bill?</div>
              <div style={{fontSize:12,color:C.muted}}>Contact iVision support</div>
            </div>
            <a href="mailto:support@ivisionmarketing.com" style={{...S.btn('outline'),textDecoration:'none',fontSize:12}}>📧 Contact Support</a>
          </div>
        </div>
      )}
    </div>
  );
}
